import api from "./client";

export type BackfillDayStatus = "pending" | "running" | "done" | "error";

// Whitelist kept in sync with
// `backend/app/schemas/backfill.py::BackfillDaysSortKey`.
export type BackfillDaysSortKey =
  | "date_desc"
  | "date_asc"
  | "attempts_desc"
  | "completed_desc"
  | "completed_asc";

export interface BackfillSummary {
  total: number;
  pending: number;
  running: number;
  done: number;
  error: number;
}

export interface BackfillDay {
  date: string;
  status: BackfillDayStatus;
  leads_count: number | null;
  completed_at: string | null;
  attempts: number;
  last_error: string | null;
}

export interface BackfillStatus {
  summary: BackfillSummary;
  active: boolean;
  active_sync_run_id: number | null;
  running_days: BackfillDay[];
  recent_days: BackfillDay[];
}

export interface BackfillDaysFilter {
  q?: string;
  status?: BackfillDayStatus;
  sort?: BackfillDaysSortKey;
  page?: number;
  per_page?: number;
}

export interface BackfillDaysPage {
  items: BackfillDay[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface BackfillSeedRequest {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

export async function getBackfillStatus(): Promise<BackfillStatus> {
  const { data } = await api.get<BackfillStatus>("/api/backfill/status");
  return data;
}

function toParams(
  f: BackfillDaysFilter,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v as string | number;
  }
  return out;
}

export async function listBackfillDays(
  filter: BackfillDaysFilter,
): Promise<BackfillDaysPage> {
  const { data } = await api.get<BackfillDaysPage>("/api/backfill/days", {
    params: toParams(filter),
  });
  return data;
}

export async function seedBackfill(
  body: BackfillSeedRequest,
): Promise<{ inserted: number }> {
  const { data } = await api.post<{ inserted: number }>(
    "/api/backfill/seed",
    body,
  );
  return data;
}

export async function runBackfill(): Promise<{
  sync_run_id: number;
  message: string;
}> {
  const { data } = await api.post<{ sync_run_id: number; message: string }>(
    "/api/backfill/run",
    {},
  );
  return data;
}

export async function retryBackfillErrors(): Promise<{ requeued: number }> {
  const { data } = await api.post<{ requeued: number }>(
    "/api/backfill/retry-errors",
    {},
  );
  return data;
}
